import { ButtonType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Formatters, Messages } from '../../codegen/Localize';
import { AngularDirective } from '../../common/react/components/AngularDirective';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { MultilineTextField } from '../../common/react/components/MultilineTextField';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { IAuthenticationProviderSelectorModel } from '../../ts/directives/tbAuthenticationProviderSelector';
import { AngularContextFactory } from '../../ts/factories/AngularContextFactory';
import { EmailValidationHelper } from '../../ts/helpers/EmailValidationHelper';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper} from '../../ts/helpers/SiteRolePickerMenuHelper';
import { L10n } from '../../ts/L10n/L10n';
import { Set } from '../../ts/models/Set';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteUsers } from '../../ts/resources/SiteUsers';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IUserActionResult, UserActionResultCode } from '../../ts/ServerApi/types/IUserActionResult';
import { AddUsersOptionsAction } from '../../ts/services/AddUsersOptionsAction';
import { ServerService } from '../../ts/services/ServerService';
import { UserActionNotification } from '../services/notifications/UserActionNotification';

export interface IAddExternalUsersDialogScope extends ISiteRolePickerMenuScope {
  authenticationProviderSelectorModel: IAuthenticationProviderSelectorModel;

  input: {
    text: string;
  };

  siteRole: string;

  userQuotaMessage: string;

  invalidEmails?: string[];
}


export interface AddExternalSiteUsersDialogState {
  readonly siteRole: string;
  readonly busy: boolean;
  readonly userInput: string;
  readonly invalidEmails: string[];
}

export interface AddExternalSiteUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly availableQuota: number;
}

export function isQuotaEnabled(availableQuota: number, isServerDomainFamilyExternal: boolean,
  hasSubscriptionTieredLicensing: boolean): boolean {
 if (isServerDomainFamilyExternal && hasSubscriptionTieredLicensing) {
   return false;
 }
 return _.isNumber(availableQuota);
}

const splitEmails = (emailText: string): string[] => _.chain(emailText.split(/[;,\r\n]+/))
                                                    .map((email: string) => email.trim())
                                                    .compact() // Remove empty strings
                                                    .unique()  // Remove dupes
                                                    .value();
export class AddExternalSiteUsersDialog extends React.Component<AddExternalSiteUsersDialogProps, AddExternalSiteUsersDialogState> {
  private dialogScope: ng.IScope = ServiceLocator.getAngularActionContext().scope;
  private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  private server: ServerService = ServiceLocator.get<ServerService>('ServerService');
  private siteUsers: SiteUsers = ServiceLocator.get<SiteUsers>('SiteUsers');
  private userActionNotification: UserActionNotification = UserActionNotification.instance;
  private AddUsersOptionsAction: AddUsersOptionsAction = ServiceLocator.get<AddUsersOptionsAction>('AddUsersOptionsAction');
  private addUserScope = this.configureScope(this.dialogScope);
  private userAngularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.addUserScope);
  public constructor(props: AddExternalSiteUsersDialogProps) {
    super(props);
    this.state = {
      siteRole:
        this.props.availableQuota <= 0 ? SiteRoles.Unlicensed : DefaultSiteRole,
      busy: false,
      userInput: '',
      invalidEmails: []
    };
  }

  private readonly importUsersCallback = () => {
    let invalidEmails = this.getInvalidEmails(this.state.userInput);
    this.setState({invalidEmails});
    if(invalidEmails.length === 0){
      this.importUsers();
    }
  }

  private readonly handleChangeCallback = (userInput: string) => {
    this.setState({userInput});
  }

  private readonly translateCallback = (key: string) => {
    return this.translate.instant(key);
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  }

  private siteRoleOptions = this.server.isSessionServerAdmin() === true ? this.siteRolesOptionResource.getAllSiteRoleOptions() : this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

  public render() {
    return (
      <div className="tb-dialog-external-users ng-scope">
        <div className="tb-dialog-title ng-binding">{Messages.addExternalUsersTitle()}</div>
        {this.renderAuthenticationProviderSelector()}
        <div>{Messages.addExternalUsersDesc()}</div>
        <div className="tb-dialog-body-control tb-multiline-text-holder">
          <MultilineTextField
            handleChange={this.handleChangeCallback}
            disabled={this.state.busy}
            placeholder={Messages.addExternalUsersPlaceholder()}
          />
        </div>
        {this.renderQuotaMessageIfApplicable()}
        {this.renderInvalidEmailsIfApplicable()}
        <div className="tb-row">
          <span className="tb-row-height-box tb-margin-right">
            {Messages.siteRole()}
          </span>
          <span className="tb-box tb-row-with-icon-on-right">
            <SiteRolePickerMenu
              translate={this.translateCallback}
              siteRoleOptions={this.siteRoleOptions}
              getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
              onOptionSelected={this.onOptionSelectedCallback}
              selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
              isDisabled={this.state.busy}
            />
          </span>
        </div>
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.importUsersCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.addUsersDialogSubmit()}
              disabled={this.state.busy || this.state.userInput.length === 0}
            />
          </span>
        </div>
      </div>
    );
  }

  public renderQuotaMessageIfApplicable() {
    if (isQuotaEnabled(this.props.availableQuota,
      this.server.isServerDomainFamilyExternal(),
      this.server.hasSubscriptionTieredLicensing())) {
      return (
        <div className={'tb-available-quota tb-row'}>
          {Messages.availableQuota({NUM_USERS: this.props.availableQuota, NUM_USERS_LOC: Formatters.formatCount(this.props.availableQuota)})}
        </div>
      );
    }
    return null;
  }

  private renderInvalidEmailsIfApplicable() {
    if (this.state.invalidEmails && this.state.invalidEmails.length > 0) {
      let translatedInvalidEmails = this.state.invalidEmails.map((invalidEmail: string) => {
        return Messages.addExternalUsersInvalidEmail({ EMAIL: invalidEmail });
      });
      return (
        <div className={'tb-dialog-warnings-list'}>
          {translatedInvalidEmails.map(function(translatedInvalidEmail: string, index) {
            return (
              <div key={index} className={'tb-dialog-invalid-email-warning'}>
                {translatedInvalidEmail}
              </div>);
          })}
        </div>
        );
    }
    return null;
  }

  private getInvalidEmails(userInput: string){
    let emails = splitEmails(userInput);
    let invalidEmails = EmailValidationHelper.getInvalidEmails(emails);
    return invalidEmails;
  }

  private renderAuthenticationProviderSelector() {
    return (
        <AngularDirective angularContext={this.userAngularContext}
                          template={`<tb:authentication-provider-selector model="authenticationProviderSelectorModel"></tb:authentication-provider-selector>`}/>
    );
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    this.setState({siteRole: siteRoleOption.role});
  }

  public importUsers(): ng.IPromise<any> {
    this.setState({busy: true});
    let emails = splitEmails(this.state.userInput);
    let scope = this.addUserScope;
    return this.siteUsers
      .importExternalUsers(emails, this.state.siteRole, scope.authenticationProviderSelectorModel.authenticationProvider.id)
      .then((result: IUserActionResult) => {
        // DO NOT SET STATE in this then clause
        this.userActionNotification.notify({
          users: Set.fromIdItemArray(result.users),
          results: result.results,
          errors: result.errors,
          successCode: UserActionResultCode.siteUserCreated,
          siteRole: this.state.siteRole
        });
        this.props.close({});
      })
      .finally(() => scope.$destroy());
  }

  public configureScope(dialogScope: ng.IScope): IAddExternalUsersDialogScope  {
    let addUserScope: IAddExternalUsersDialogScope = dialogScope.$new() as IAddExternalUsersDialogScope;
    addUserScope.input = {
      text: ''
    };
    addUserScope.authenticationProviderSelectorModel = this.AddUsersOptionsAction.getInitialAuthenticationProviderSelectorModel();

    addUserScope.siteRole = DefaultSiteRole;
    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      addUserScope.$apply(() => {
        addUserScope.siteRole = roleOption.role; // also set $scope.siteRole so original behavior is unaltered
        SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.translate, this.server, this.siteRolesOptionResource, addUserScope.siteRole, onSiteRoleSelected, true);
      });
    };

    SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.translate, this.server, this.siteRolesOptionResource, addUserScope.siteRole, onSiteRoleSelected, true);
    return addUserScope;
  }
}
